import React, { useState, useEffect } from 'react';
import { BasicInfo } from './types';
import { theme } from '../../styles/theme';
import {
  AboutEditorContainer,
  AboutEditorHeader,
  AboutEditorTitle,
  AboutEditorDescription,
  AboutEditorContent,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  AboutEditorActions,
  ActionButton,
  ValidationMessage,
  AboutEditorTips,
  TipItem
} from '../../styles/Profile/ProfileEditor.styles';
import { RequiredIndicator } from '../../styles/Profile/ProfileSetup.styles';
import { DateOfBirthSelector, HeightSelector, LocationSelector } from '../shared';

interface AboutEditorProps {
  basicInfo: BasicInfo;
  onChange: (basicInfo: BasicInfo) => void;
}

const ABOUT_TIPS = [
  "Keep your information accurate and up-to-date",
  "Your occupation helps others understand your lifestyle",
  "Education level can help find compatible matches",
  "Be honest about your age and gender",
  "Update your profile regularly to keep it fresh",
  "Complete all fields to increase your match potential",
  "Be authentic - honesty leads to better connections"
];

export const AboutEditor: React.FC<AboutEditorProps> = ({
  basicInfo,
  onChange
}) => {
  const [formData, setFormData] = useState<BasicInfo>(basicInfo);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setFormData(basicInfo);
  }, [basicInfo]);

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'firstName':
        if (!value || value.trim().length < 2) {
          return 'First name must be at least 2 characters';
        }
        break;
      case 'lastName':
        if (!value || value.trim().length < 2) {
          return 'Last name must be at least 2 characters';
        }
        break;
      case 'dateOfBirth': {
        if (!value) {
          return 'Date of birth is required';
        }
        const age = new Date().getFullYear() - new Date(value).getFullYear();
        if (age < 18) {
          return 'You must be at least 18 years old';
        }
        if (age > 100) {
          return 'Please enter a valid date of birth';
        }
        break;
      }
      case 'gender':
        if (!value) {
          return 'Gender is required';
        }
        break;
      case 'occupation':
        if (value && value.trim().length < 2) {
          return 'Occupation must be at least 2 characters';
        }
        break;
      case 'education':
        if (value && value.trim().length < 2) {
          return 'Education must be at least 2 characters';
        }
        break;
    }
    return '';
  };

  const handleFieldChange = (field: keyof BasicInfo, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    if (touched[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }

    onChange(newFormData);
  };

  const handleBlur = (field: keyof BasicInfo) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  return (
    <AboutEditorContainer>
      <AboutEditorHeader>
        <AboutEditorTitle>About You</AboutEditorTitle>
        <AboutEditorDescription>
          Share your basic information to help others get to know you better.
        </AboutEditorDescription>
      </AboutEditorHeader>



      <AboutEditorContent>
        <FormGroup>
          <FormLabel>
            First Name <RequiredIndicator>*</RequiredIndicator>
          </FormLabel>
          <FormInput
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            placeholder="Enter your first name"
            $hasError={touched.firstName && !!errors.firstName}
          />
          {touched.firstName && errors.firstName && (
            <ValidationMessage $type="error">
              {errors.firstName}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>
            Last Name <RequiredIndicator>*</RequiredIndicator>
          </FormLabel>
          <FormInput
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            placeholder="Enter your last name"
            $hasError={touched.lastName && !!errors.lastName}
          />
          {touched.lastName && errors.lastName && (
            <ValidationMessage $type="error">
              {errors.lastName}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>
            Date of Birth <RequiredIndicator>*</RequiredIndicator>
          </FormLabel>
          <DateOfBirthSelector
            value={formData.dateOfBirth}
            onChange={(date) => handleFieldChange('dateOfBirth', date)}
            onBlur={() => handleBlur('dateOfBirth')}
            hasError={touched.dateOfBirth && !!errors.dateOfBirth}
          />
          {touched.dateOfBirth && errors.dateOfBirth && (
            <ValidationMessage $type="error">
              {errors.dateOfBirth}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>
            Gender <RequiredIndicator>*</RequiredIndicator>
          </FormLabel>
          <FormSelect
            value={formData.gender || 'other'}
            onChange={(e) => handleFieldChange('gender', e.target.value)}
            onBlur={() => handleBlur('gender')}
            $hasError={touched.gender && !!errors.gender}
            title="Select your gender"
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="non-binary">Non-binary</option>
            <option value="other">Other</option>
          </FormSelect>
          {touched.gender && errors.gender && (
            <ValidationMessage $type="error">
              {errors.gender}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Location</FormLabel>
          <LocationSelector
            value={formData.location}
            onChange={(location) => handleFieldChange('location', location)}
            onBlur={() => handleBlur('location')}
            hasError={touched.location && !!errors.location}
          />
          {touched.location && errors.location && (
            <ValidationMessage $type="error">
              {errors.location}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Occupation</FormLabel>
          <FormInput
            type="text"
            value={formData.occupation || ''}
            onChange={(e) => handleFieldChange('occupation', e.target.value)}
            onBlur={() => handleBlur('occupation')}
            placeholder="e.g., Software Engineer, Teacher, Artist"
            $hasError={touched.occupation && !!errors.occupation}
          />
          {touched.occupation && errors.occupation && (
            <ValidationMessage $type="error">
              {errors.occupation}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Education</FormLabel>
          <FormSelect
            value={formData.education || ''}
            onChange={(e) => handleFieldChange('education', e.target.value)}
            onBlur={() => handleBlur('education')}
            $hasError={touched.education && !!errors.education}
            title="Select your education level"
          >
            <option value="">Select education level</option>
            <option value="High School">High School</option>
            <option value="Some College">Some College</option>
            <option value="Associate's Degree">Associate's Degree</option>
            <option value="Bachelor's Degree">Bachelor's Degree</option>
            <option value="Master's Degree">Master's Degree</option>
            <option value="Doctorate">Doctorate</option>
            <option value="Professional Degree">Professional Degree</option>
            <option value="Other">Other</option>
          </FormSelect>
          {touched.education && errors.education && (
            <ValidationMessage $type="error">
              {errors.education}
            </ValidationMessage>
          )}
        </FormGroup>

        {/* Dating Profile Fields */}
        <FormGroup>
          <FormLabel>Height</FormLabel>
          <HeightSelector
            value={formData.height}
            onChange={(height) => handleFieldChange('height', height)}
            onBlur={() => handleBlur('height')}
            hasError={touched.height && !!errors.height}
          />
          {touched.height && errors.height && (
            <ValidationMessage $type="error">
              {errors.height}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Body Type</FormLabel>
          <FormSelect
            value={formData.bodyType || ''}
            onChange={(e) => handleFieldChange('bodyType', e.target.value)}
            onBlur={() => handleBlur('bodyType')}
            title="Select your body type"
          >
            <option value="">Select body type</option>
            <option value="slim">Slim</option>
            <option value="athletic">Athletic</option>
            <option value="average">Average</option>
            <option value="curvy">Curvy</option>
            <option value="muscular">Muscular</option>
            <option value="plus-size">Plus-size</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Relationship Status</FormLabel>
          <FormSelect
            value={formData.relationshipStatus || ''}
            onChange={(e) => handleFieldChange('relationshipStatus', e.target.value)}
            onBlur={() => handleBlur('relationshipStatus')}
            title="Select your relationship status"
          >
            <option value="">Select relationship status</option>
            <option value="single">Single</option>
            <option value="divorced">Divorced</option>
            <option value="widowed">Widowed</option>
            <option value="separated">Separated</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Looking For</FormLabel>
          <FormSelect
            value={formData.lookingFor || ''}
            onChange={(e) => handleFieldChange('lookingFor', e.target.value)}
            onBlur={() => handleBlur('lookingFor')}
            title="What are you looking for?"
          >
            <option value="">Select what you're looking for</option>
            <option value="dating">Dating</option>
            <option value="friendship">Friendship</option>
            <option value="serious">Serious Relationship</option>
            <option value="casual">Casual Relationship</option>
            <option value="long-term">Long-term Relationship</option>
            <option value="short-term">Short-term Relationship</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Drinking</FormLabel>
          <FormSelect
            value={formData.drinking || ''}
            onChange={(e) => handleFieldChange('drinking', e.target.value)}
            onBlur={() => handleBlur('drinking')}
            title="Your drinking habits"
          >
            <option value="">Select drinking preference</option>
            <option value="never">Never</option>
            <option value="socially">Socially</option>
            <option value="regularly">Regularly</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Smoking</FormLabel>
          <FormSelect
            value={formData.smoking || ''}
            onChange={(e) => handleFieldChange('smoking', e.target.value)}
            onBlur={() => handleBlur('smoking')}
            title="Your smoking habits"
          >
            <option value="">Select smoking preference</option>
            <option value="never">Never</option>
            <option value="socially">Socially</option>
            <option value="regularly">Regularly</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Exercise</FormLabel>
          <FormSelect
            value={formData.exercise || ''}
            onChange={(e) => handleFieldChange('exercise', e.target.value)}
            onBlur={() => handleBlur('exercise')}
            title="Your exercise habits"
          >
            <option value="">Select exercise frequency</option>
            <option value="never">Never</option>
            <option value="occasionally">Occasionally</option>
            <option value="regularly">Regularly</option>
            <option value="daily">Daily</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Religion</FormLabel>
          <FormSelect
            value={formData.religion || ''}
            onChange={(e) => handleFieldChange('religion', e.target.value)}
            onBlur={() => handleBlur('religion')}
            title="Select your religion"
          >
            <option value="">Select religion</option>
            <option value="christian">Christian</option>
            <option value="catholic">Catholic</option>
            <option value="jewish">Jewish</option>
            <option value="muslim">Muslim</option>
            <option value="buddhist">Buddhist</option>
            <option value="hindu">Hindu</option>
            <option value="spiritual">Spiritual</option>
            <option value="atheist">Atheist</option>
            <option value="agnostic">Agnostic</option>
            <option value="other">Other</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Political Views</FormLabel>
          <FormSelect
            value={formData.politicalViews || ''}
            onChange={(e) => handleFieldChange('politicalViews', e.target.value)}
            onBlur={() => handleBlur('politicalViews')}
            title="Select your political views"
          >
            <option value="">Select political views</option>
            <option value="very-liberal">Very Liberal</option>
            <option value="liberal">Liberal</option>
            <option value="moderate">Moderate</option>
            <option value="conservative">Conservative</option>
            <option value="very-conservative">Very Conservative</option>
            <option value="other">Other</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Languages</FormLabel>
          <FormInput
            type="text"
            value={formData.languages?.join(', ') || ''}
            onChange={(e) => {
              const languages = e.target.value.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0);
              handleFieldChange('languages', languages);
            }}
            onBlur={() => handleBlur('languages')}
            placeholder="e.g., English, Spanish, French"
            $hasError={touched.languages && !!errors.languages}
          />
          {touched.languages && errors.languages && (
            <ValidationMessage $type="error">
              {errors.languages}
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <FormLabel>Pets</FormLabel>
          <FormSelect
            value={formData.pets || ''}
            onChange={(e) => handleFieldChange('pets', e.target.value)}
            onBlur={() => handleBlur('pets')}
            title="Select your pet preference"
          >
            <option value="">Select pet preference</option>
            <option value="dogs">Dogs</option>
            <option value="cats">Cats</option>
            <option value="other">Other</option>
            <option value="none">None</option>
            <option value="allergic">Allergic</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Kids</FormLabel>
          <FormSelect
            value={formData.kids || ''}
            onChange={(e) => handleFieldChange('kids', e.target.value)}
            onBlur={() => handleBlur('kids')}
            title="Select your kids preference"
          >
            <option value="">Select kids preference</option>
            <option value="wants-kids">Wants kids</option>
            <option value="has-kids">Has kids</option>
            <option value="doesnt-want-kids">Doesn't want kids</option>
            <option value="has-kids-wants-more">Has kids, wants more</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormLabel>Zodiac Sign (Optional)</FormLabel>
          <FormSelect
            value={formData.zodiac || ''}
            onChange={(e) => handleFieldChange('zodiac', e.target.value)}
            onBlur={() => handleBlur('zodiac')}
            $hasError={touched.zodiac && !!errors.zodiac}
            title="Select your zodiac sign"
          >
            <option value="">Select zodiac sign</option>
            <option value="Aries">Aries</option>
            <option value="Taurus">Taurus</option>
            <option value="Gemini">Gemini</option>
            <option value="Cancer">Cancer</option>
            <option value="Leo">Leo</option>
            <option value="Virgo">Virgo</option>
            <option value="Libra">Libra</option>
            <option value="Scorpio">Scorpio</option>
            <option value="Sagittarius">Sagittarius</option>
            <option value="Capricorn">Capricorn</option>
            <option value="Aquarius">Aquarius</option>
            <option value="Pisces">Pisces</option>
          </FormSelect>
          {touched.zodiac && errors.zodiac && (
            <ValidationMessage $type="error">
              {errors.zodiac}
            </ValidationMessage>
          )}
        </FormGroup>
      </AboutEditorContent>

      <AboutEditorTips>
        <h4>Tips for Your About Section</h4>
        {ABOUT_TIPS.map((tip, index) => (
          <TipItem key={index}>{tip}</TipItem>
        ))}
      </AboutEditorTips>
    </AboutEditorContainer>
  );
};

